/**
 * Helper utilities for the Now Assist Skill Plugin
 * Contains common functions used across skill processing
 */

import {
    TABLE_DEFAULTS,
    RESOURCE_EDGE_APPLICABILITY_SCRIPT,
    RESOURCE_EDGE_ERROR_HANDLER_SCRIPT,
    TOOL_RESOURCE_CAPABILITIES,
} from './defaults'
import type { Context, ObjectShape, Record as BuildRecord, Shape } from '@servicenow/sdk-build-core'

/**
 * Helper function to get field values from additionalFields with proper type safety.
 * Automatically looks up default values from centralized defaults in defaults.ts.
 *
 * @param source - The additionalFields object
 * @param tableName - The table name to look up (e.g., SN_NOWASSIST_SKILL_CONFIG)
 * @param fieldName - The field name to retrieve
 * @param defaultValue - Optional explicit default value (takes precedence over defaults.ts)
 * @returns The field value, explicit default, or default from defaults.ts
 *
 * Priority order:
 * 1. Value from source[tableName][fieldName] if it exists
 * 2. Explicit defaultValue parameter if provided
 * 3. Default from TABLE_DEFAULTS[tableName][fieldName]
 * 4. undefined if none of the above exist
 */
export function getField<T = unknown>(
    source: Record<string, Record<string, unknown>>,
    tableName: string,
    fieldName: string,
    defaultValue?: T
): T | undefined {
    const tableValue = source?.[tableName]

    // Check if the field exists in the source
    if (tableValue && typeof tableValue === 'object') {
        const fieldValue = tableValue[fieldName]
        if (fieldValue !== undefined) {
            return fieldValue as T
        }
    }

    // If explicit defaultValue is provided, use it
    if (defaultValue !== undefined) {
        return defaultValue
    }

    // Otherwise, look up the default from the centralized defaults
    const tableDefaults = TABLE_DEFAULTS[tableName]
    if (tableDefaults && fieldName in tableDefaults) {
        return tableDefaults[fieldName] as T
    }

    return undefined
}

/**
 * Helper function to sanitize skill name for internal_name field.
 * @param name - The skill name to sanitize
 * @returns The sanitized name in lowercase with underscores
 */
export function sanitizeSkillName(name: string): string {
    if (!name) {
        return ''
    }
    return name.toString().trim().replace(/\\/g, '').replace(/\s+/g, '_').toLowerCase()
}

/**
 * Apply table defaults to properties object.
 * Wraps each default value with $.val() for use in arg.transform().
 *
 * @param tableName - Name of the table to look up defaults for
 * @param $ - Transform dollar sign object with val method
 * @returns Object with default values applied
 */
export function applyDefaults(tableName: string, $: { val: (value: unknown) => unknown }): Record<string, unknown> {
    const defaults = TABLE_DEFAULTS[tableName] || {}
    return Object.fromEntries(Object.entries(defaults).map(([key, value]) => [key, $.val(value)]))
}

/**
 * Build a depends_on_list string from dependency tool names.
 * Resolves the dependency names to their record IDs via toolRecordsMap.
 *
 * @param tool - Tool definition containing depends (array of dependency names) and name
 * @param toolRecordsMap - Map from tool name to its resource mapping record
 * @param arg - The argument shape for diagnostics source location
 * @param diagnostics - Diagnostics for warning about missing dependencies
 * @returns Comma-separated list of dependency record sys_ids, or empty string
 */
export function buildDependsOnList(
    tool: { name: string; depends?: string[] | undefined },
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): string {
    if (!tool.depends || tool.depends.length === 0) {
        return ''
    }
    const depIds: string[] = []
    for (const depName of tool.depends) {
        const depRecord = toolRecordsMap.get(depName)
        if (!depRecord) {
            // Don't warn if the dependency is a Decision node - Decision routing edges
            // are created separately by createDecisionEdges, so this is expected.
            if (!decisionToolNames?.has(depName)) {
                diagnostics.warn(arg, `Dependency '${depName}' not found for tool '${tool.name}'`)
            }
            continue
        }
        depIds.push(depRecord.getId().toString().getValue())
    }
    return depIds.join(',')
}

/**
 * Create sys_one_extend_resource_edge records for tool dependencies.
 *
 * @param tool - Tool definition containing depends (array of dependency names) and name
 * @param toolRecordsMap - Map from tool name to its resource mapping record
 * @param resourceMappingRecord - The current tool's resource mapping record (edge target)
 * @param arg - The argument shape for transformations
 * @param factory - The record factory
 * @param includeScripts - Whether to include applicability_script and error_handler_script on edges.
 *                         InlineScript edges omit these; all other tool types include them.
 * @returns Array of created edge records
 */
export async function createDependencyEdges(
    tool: { name: string; depends?: string[] | undefined },
    toolRecordsMap: Map<string, BuildRecord>,
    resourceMappingRecord: BuildRecord,
    arg: ObjectShape,
    factory: Context['factory'],
    includeScripts: boolean = true,
    source: Shape
): Promise<BuildRecord[]> {
    const edgeRecords: BuildRecord[] = []
    if (!tool.depends || tool.depends.length === 0) {
        return edgeRecords
    }

    for (const depName of tool.depends) {
        const depRecord = toolRecordsMap.get(depName)
        if (!depRecord) {
            continue // Already warned during buildDependsOnList
        }

        // Skip creating dependency edge if the dependency is a Decision node.
        // Decision routing edges are created by createDecisionEdges() instead,
        // which handles the targets array and branch conditions properly.
        const depResourceCapability = depRecord.get('resource_capability')?.getValue() as string
        if (depResourceCapability === TOOL_RESOURCE_CAPABILITIES.DECISION) {
            continue
        }

        const edgeRecord = await factory.createRecord({
            source: source ?? arg,
            table: 'sys_one_extend_resource_edge',
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults('sys_one_extend_resource_edge', $),
                ...(includeScripts
                    ? {
                          applicability_script: $.val(RESOURCE_EDGE_APPLICABILITY_SCRIPT).toCdata(),
                          error_handler_script: $.val(RESOURCE_EDGE_ERROR_HANDLER_SCRIPT).toCdata(),
                      }
                    : {}),
                from_resource: $.val(depRecord.getId()),
                internal_name: $.val(`${depName}__${tool.name}`),
                to_resource: $.val(resourceMappingRecord.getId()),
            })),
        })
        edgeRecords.push(edgeRecord)
    }

    return edgeRecords
}
